import React from 'react';
import { graphql, Link } from 'gatsby';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Layout from '../components/layout/Layout';

const BooksHero = styled.section`
  min-height: 50vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: ${props => props.theme.space[20]} ${props => props.theme.space[6]};
  background: linear-gradient(
    135deg,
    var(--color-bg) 0%,
    var(--color-surface) 100%
  );
  position: relative;
`;

const HeroContent = styled.div`
  max-width: ${props => props.theme.containers.lg};
  margin: 0 auto;
  text-align: center;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSizes['6xl']};
  margin-bottom: ${props => props.theme.space[6]};
  background: linear-gradient(
    135deg,
    ${props => props.theme.colors.secondary} 0%,
    ${props => props.theme.colors.accent} 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.typography.fontSizes['4xl']};
  }
`;

const Quote = styled.blockquote`
  max-width: 800px;
  margin: 0 auto;
  font-size: ${props => props.theme.typography.fontSizes.lg};
  font-style: italic;
  color: var(--color-text-secondary);
  line-height: ${props => props.theme.typography.lineHeights.relaxed};
  border-left: 4px solid ${props => props.theme.colors.secondary};
  padding-left: ${props => props.theme.space[6]};
  margin-top: ${props => props.theme.space[8]};
`;

const BooksSection = styled.section`
  max-width: ${props => props.theme.containers.xl};
  margin: 0 auto;
  padding: ${props => props.theme.space[16]} ${props => props.theme.space[6]};
`;

const BookGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${props => props.theme.space[8]};
`;

const BookCard = styled(Link)`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: ${props => props.theme.radii.xl};
  padding: ${props => props.theme.space[6]};
  transition: all 0.3s ease;
  text-decoration: none;
  display: flex;
  gap: ${props => props.theme.space[4]};

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${props => props.theme.shadows.xl};
    border-color: ${props => props.theme.colors.secondary};
  }
`;

const BookCover = styled.div`
  flex-shrink: 0;
  width: 100px;
  height: 150px;
  background: ${props => props.coverImage
    ? `url(${props.coverImage}) center/cover no-repeat`
    : `linear-gradient(
        135deg,
        rgba(0, 78, 137, 0.1) 0%,
        rgba(255, 107, 53, 0.1) 100%
      )`};
  border-radius: ${props => props.theme.radii.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${props => props.theme.typography.fontSizes['4xl']};
  color: ${props => props.theme.colors.secondary};
  opacity: ${props => props.coverImage ? 1 : 0.3};
  box-shadow: ${props => props.coverImage ? props.theme.shadows.md : 'none'};
`;

const BookInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.space[2]};
`;

const BookTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSizes.xl};
  color: var(--color-text);
  margin: 0;
`;

const BookMeta = styled.p`
  font-size: ${props => props.theme.typography.fontSizes.sm};
  color: var(--color-text-secondary);
  margin: 0;
`;

function BooksPage({ data }) {
  const { t } = useTranslation();
  const books = data.allMarkdownRemark.nodes;

  return (
    <Layout
      title={t('books.title', 'Bookshelf')}
      description="Books I'm reading, have read, or will read"
      path="/books"
    >
      <BooksHero>
        <HeroContent>
          <Title>{t('books.title', 'Bookshelf')}</Title>
          <Quote>
            {t('books.quote')}
            <br />
            <cite>— {t('books.quoteSource', 'Carl Sagan, Cosmos')}</cite>
          </Quote>
        </HeroContent>
      </BooksHero>

      <BooksSection>
        {books.length > 0 ? (
          <BookGrid>
            {books.map((book) => {
              const lang = book.frontmatter.lang || book.fields?.language || 'en-us';
              const basePath = lang === 'en-us' ? '/books' : `/${lang}/libros`;
              const slug = book.fields?.slug || book.frontmatter.page_id;
              const path = `${basePath}/${slug}`;

              // Handle cover image - can be a local path or URL
              let coverImage = null;
              if (book.frontmatter.cover) {
                const cover = book.frontmatter.cover;
                // If it's a URL, use it directly
                if (cover.startsWith('http://') || cover.startsWith('https://')) {
                  coverImage = cover;
                } else {
                  // If it's a local path, convert it to the static path
                  // Remove 'assets/' prefix if present since our static folder is already /assets/
                  const cleanPath = cover.replace(/^assets\//, '/assets/');
                  coverImage = cleanPath;
                }
              }

              return (
                <BookCard key={book.id} to={path}>
                  <BookCover coverImage={coverImage}>
                    {!coverImage && '📖'}
                  </BookCover>
                  <BookInfo>
                    <BookTitle>{book.frontmatter.title}</BookTitle>
                    {book.frontmatter.description && (
                      <BookMeta>{book.frontmatter.description}</BookMeta>
                    )}
                  </BookInfo>
                </BookCard>
              );
            })}
          </BookGrid>
        ) : (
          <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--color-text-secondary)' }}>
            <h2>{t('books.empty', 'No books yet')}</h2>
            <p>{t('books.emptyDescription', 'Check back soon for book reviews!')}</p>
          </div>
        )}
      </BooksSection>
    </Layout>
  );
}

export default BooksPage;

export const query = graphql`
  query BooksPageQuery($language: String!) {
    allMarkdownRemark(
      filter: {
        frontmatter: { type: { eq: "books" }, lang: { eq: $language } }
      }
      sort: { frontmatter: { date: DESC } }
    ) {
      nodes {
        id
        fields {
          slug
          language
        }
        frontmatter {
          page_id
          title
          description
          cover
          lang
        }
      }
    }
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;
